import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markNotificationsSeen } from '../lib/api';
import { Menu, X, Target, User, LogOut, Settings, Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_94731faa-b5ac-464b-a582-e1eba7087aed/artifacts/0s0e8623_Untitled_design.png';

const navItems = [
  { name: 'Startseite', path: '/' },
  { name: 'Mannschaften', path: '/mannschaften' },
  { name: 'Spielplan', path: '/spielplan' },
  { name: 'Termine', path: '/termine' },
  { name: 'News', path: '/news' },
  { name: 'Galerie', path: '/galerie' },
  { name: 'Sponsoren', path: '/sponsoren' },
  { name: 'Kontakt', path: '/kontakt' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  useEffect(() => {
    if (!user) return;
    const fetchNotifs = async () => {
      try {
        const res = await getNotifications();
        setNotifications(res.data.notifications || []);
        setUnread(res.data.unread || 0);
      } catch (e) { /* not logged in */ }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleOpenNotifs = async () => {
    setNotifOpen(!notifOpen);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="main-navbar">
      <div className="container-max">
        <div className="flex items-center justify-between h-16 md:h-20 px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="House of Darts" className="h-10 md:h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                  isActive(item.path)
                    ? 'text-primary bg-white/5'
                    : 'text-text-secondary hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {user && (
              <div className="relative">
                <Button variant="ghost" size="sm" onClick={handleOpenNotifs} className="relative text-text-secondary hover:text-white" data-testid="notification-bell">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </Button>
                {notifOpen && (
                  <div className="absolute right-0 top-10 w-80 bg-surface border border-white/10 rounded-md shadow-xl z-50 max-h-80 overflow-y-auto" data-testid="notification-dropdown">
                    <div className="p-3 border-b border-white/10">
                      <p className="text-sm font-heading font-bold uppercase">Benachrichtigungen</p>
                    </div>
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-white/5">
                        {notifications.slice(0, 10).map(n => (
                          <div key={n.notification_id} className={`p-3 text-sm ${n.is_new ? 'bg-primary/5' : ''}`}>
                            <p className={`font-medium ${n.is_new ? 'text-white' : 'text-text-secondary'}`}>{n.title}</p>
                            <p className="text-text-secondary text-xs mt-0.5">{n.message}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 text-center text-text-secondary text-sm">Keine Benachrichtigungen</div>
                    )}
                  </div>
                )}
              </div>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 text-text-secondary hover:text-white" data-testid="user-menu-trigger">
                    <User className="w-4 h-4" />
                    <span>{user.name || user.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-surface border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to="/profil" className="flex items-center gap-2" data-testid="profile-link">
                      <User className="w-4 h-4" />
                      Mein Profil
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                        <Settings className="w-4 h-4" />
                        Admin-Bereich
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-primary" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" className="text-text-secondary hover:text-white">
                    Anmelden
                  </Button>
                </Link>
                <Link to="/mitglied-werden" data-testid="member-cta">
                  <Button className="btn-primary">
                    Mitglied werden
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-text-secondary hover:text-white"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 px-4 border-t border-white/10" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 text-sm font-medium transition-colors rounded-sm ${
                    isActive(item.path)
                      ? 'text-primary bg-white/5'
                      : 'text-text-secondary hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="border-t border-white/10 mt-2 pt-2">
                {user ? (
                  <>
                    <button
                      onClick={() => { handleOpenNotifs(); }}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-text-secondary hover:text-white w-full text-left"
                      data-testid="mobile-notification-bell"
                    >
                      <Bell className="w-4 h-4" />
                      Benachrichtigungen
                      {unread > 0 && <span className="ml-auto bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{unread}</span>}
                    </button>
                    {notifOpen && notifications.length > 0 && (
                      <div className="mx-4 mb-2 bg-surface-elevated rounded-sm border border-white/10 max-h-48 overflow-y-auto">
                        {notifications.slice(0, 5).map(n => (
                          <div key={n.notification_id} className={`p-2.5 text-xs border-b border-white/5 last:border-0 ${n.is_new ? 'bg-primary/5' : ''}`}>
                            <p className="font-medium">{n.title}</p>
                            <p className="text-text-secondary">{n.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <Link
                      to="/profil"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-text-secondary hover:text-white"
                    >
                      <User className="w-4 h-4" />
                      Mein Profil
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 text-sm text-text-secondary hover:text-white"
                      >
                        <Settings className="w-4 h-4" />
                        Admin-Bereich
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setIsOpen(false); }}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-primary w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      Abmelden
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-3 text-sm text-text-secondary hover:text-white"
                    >
                      Anmelden
                    </Link>
                    <Link
                      to="/mitglied-werden"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-3 text-sm font-semibold text-primary"
                    >
                      Mitglied werden
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-black border-t border-white/10 py-16 md:py-20" data-testid="main-footer">
      <div className="container-max px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Info */}
          <div className="lg:col-span-2">
            <img src={LOGO_URL} alt="House of Darts" className="h-16 w-auto mb-6" />
            <p className="text-text-secondary max-w-md">
              House of Darts - Dein Dartverein in Straßwalchen. 
              ZVR: 1916614880 | Marktplatz 11, 5204 Straßwalchen, Österreich
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-heading font-bold uppercase tracking-tight mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/mitglied-werden" className="text-text-secondary hover:text-primary transition-colors">Mitglied werden</Link></li>
              <li><Link to="/termine" className="text-text-secondary hover:text-primary transition-colors">Termine</Link></li>
              <li><Link to="/spielplan" className="text-text-secondary hover:text-primary transition-colors">Spielplan</Link></li>
              <li><Link to="/kontakt" className="text-text-secondary hover:text-primary transition-colors">Kontakt</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-lg font-heading font-bold uppercase tracking-tight mb-4">Rechtliches</h4>
            <ul className="space-y-2">
              <li><Link to="/impressum" className="text-text-secondary hover:text-primary transition-colors">Impressum</Link></li>
              <li><Link to="/datenschutz" className="text-text-secondary hover:text-primary transition-colors">Datenschutz</Link></li>
              <li><Link to="/kontakt" className="text-text-secondary hover:text-primary transition-colors">Kontakt</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-text-secondary text-sm">
            © {new Date().getFullYear()} House of Darts
          </p>
          <div className="flex items-center gap-2 text-accent">
            <Target className="w-5 h-5" />
            <span className="font-heading font-bold uppercase tracking-tight">Triple 20!</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
